import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Swords } from "lucide-react";
import { PageHeader, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const STEPS = ["Class", "Specialization", "Stats", "Name & Confirm"];

export default function CreateFighter() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [constants, setConstants] = useState(null);

  // Fighter data
  const [selectedClass, setSelectedClass] = useState("");
  const [classSpecialist, setClassSpecialist] = useState("");
  const [tacticSpecialist, setTacticSpecialist] = useState("");
  const [stats, setStats] = useState({
    strength: 100,
    speed: 100,
    defense: 100,
    stamina: 100,
    technique: 100,
    precision: 100
  });
  const [name, setName] = useState("");

  useEffect(() => {
    loadConstants();
  }, []);

  const loadConstants = async () => {
    try {
      const { data } = await api.get("/arena/constants");
      setConstants(data);
    } catch (e) {
      toast.error("Failed to load fighter creation data");
    }
  };

  const totalStats = Object.values(stats).reduce((sum, val) => sum + val, 0);
  const targetStats = constants ? (constants.base_stats * constants.skills.length + constants.bonus_points) : 1100;

  const setStat = (skill, value) => {
    const numValue = parseInt(value, 10) || constants?.min_stat || 100;
    const clamped = Math.max(constants?.min_stat || 100, Math.min(constants?.max_stat || 300, numValue));
    setStats({ ...stats, [skill]: clamped });
  };

  const canProgress = () => {
    if (step === 0) return selectedClass !== "";
    if (step === 1) return classSpecialist !== "" && tacticSpecialist !== "";
    if (step === 2) return totalStats === targetStats;
    if (step === 3) return name.trim().length >= 2;
    return false;
  };

  const handleCreate = async () => {
    if (!canProgress()) return;

    setBusy(true);
    try {
      const { data } = await api.post("/arena/fighters/create", {
        name: name.trim(),
        cls: selectedClass,
        class_specialist: classSpecialist,
        tactic_specialist: tacticSpecialist,
        stats
      });

      toast.success(data.message, { icon: "⚔️" });
      navigate("/arena-circuit");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!constants) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-zinc-500">Loading gladiator creation...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <PageHeader
        icon={Swords}
        title="Create Arena Gladiator"
        subtitle="Build your gladiator for the Arena Circuit"
      />

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((stepName, idx) => (
          <div key={idx} className="flex items-center flex-1">
            <div className={`flex items-center gap-2 ${idx === step ? "text-amber-400" : idx < step ? "text-emerald-400" : "text-zinc-600"}`}>
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-cinzel text-sm ${idx === step ? "border-amber-400 bg-amber-900/20" : idx < step ? "border-emerald-400 bg-emerald-900/20" : "border-zinc-700"}`}>
                {idx + 1}
              </div>
              <span className="text-sm font-cinzel hidden sm:inline">{stepName}</span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${idx < step ? "bg-emerald-400" : "bg-zinc-700"}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="border border-amber-900/30 bg-zinc-950 p-6">
        {/* Step 0: Class Selection */}
        {step === 0 && (
          <div>
            <h3 className="font-cinzel text-xl text-amber-300 mb-4">Choose Your Fighter Class</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {constants.classes.map((cls) => (
                <button
                  key={cls}
                  onClick={() => setSelectedClass(cls)}
                  className={`p-6 border-2 transition-all text-left ${
                    selectedClass === cls
                      ? "border-amber-500 bg-amber-900/20"
                      : "border-zinc-800 hover:border-amber-700/50"
                  }`}
                >
                  <h4 className="font-cinzel text-lg text-amber-300 capitalize mb-2">{cls}</h4>
                  <p className="text-sm text-zinc-400">
                    {cls === "retiarius" && "Net fighter with reach advantage"}
                    {cls === "provocator" && "Shield & sword, balanced fighter"}
                    {cls === "dimachaerus" && "Dual swords, high offense"}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Specialization */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-cinzel text-xl text-amber-300 mb-4">Class Specialist</h3>
              <p className="text-sm text-zinc-400 mb-4">Choose which fighter type you excel against</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {constants.class_specialists.map((spec) => (
                  <button
                    key={spec.id}
                    onClick={() => setClassSpecialist(spec.id)}
                    className={`p-4 border transition-all text-left ${
                      classSpecialist === spec.id
                        ? "border-amber-500 bg-amber-900/20"
                        : "border-zinc-800 hover:border-amber-700/50"
                    }`}
                  >
                    <h4 className="font-cinzel text-sm text-amber-300 mb-1">{spec.name}</h4>
                    <p className="text-xs text-zinc-500">{spec.bonus}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-cinzel text-xl text-amber-300 mb-4">Tactical Specialist</h3>
              <p className="text-sm text-zinc-400 mb-4">Choose your preferred fighting style</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {constants.tactic_specialists.map((spec) => (
                  <button
                    key={spec.id}
                    onClick={() => setTacticSpecialist(spec.id)}
                    className={`p-4 border transition-all text-left ${
                      tacticSpecialist === spec.id
                        ? "border-amber-500 bg-amber-900/20"
                        : "border-zinc-800 hover:border-amber-700/50"
                    }`}
                  >
                    <h4 className="font-cinzel text-sm text-amber-300 mb-1">{spec.name}</h4>
                    <p className="text-xs text-zinc-500">{spec.bonus}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Stat Allocation */}
        {step === 2 && (
          <div>
            <h3 className="font-cinzel text-xl text-amber-300 mb-2">Allocate Your Stats</h3>
            <p className="text-sm text-zinc-400 mb-4">
              Base {constants.base_stats} in each stat + {constants.bonus_points} bonus points to distribute
            </p>

            <div className="space-y-3 mb-4">
              {constants.skills.map((skill) => (
                <div key={skill} className="flex items-center gap-3">
                  <span className="w-24 text-sm capitalize text-zinc-400 font-cinzel">{skill}</span>
                  <input
                    type="range"
                    min={constants.min_stat}
                    max={constants.max_stat}
                    value={stats[skill]}
                    onChange={(e) => setStat(skill, e.target.value)}
                    className="flex-1 accent-amber-500"
                  />
                  <input
                    type="number"
                    min={constants.min_stat}
                    max={constants.max_stat}
                    value={stats[skill]}
                    onChange={(e) => setStat(skill, e.target.value)}
                    className="w-20 bg-zinc-900 border border-zinc-800 px-2 py-1 text-sm text-zinc-200 text-right focus:border-amber-600 outline-none"
                  />
                </div>
              ))}
            </div>

            <div className={`text-sm font-cinzel flex justify-between p-3 border ${totalStats === targetStats ? "text-emerald-400 border-emerald-700/50 bg-emerald-900/20" : "text-amber-400 border-amber-700/50 bg-amber-900/20"}`}>
              <span>Total Allocated</span>
              <span className="tabular-nums">{totalStats} / {targetStats}</span>
            </div>
          </div>
        )}

        {/* Step 3: Name & Confirm */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <label className="text-sm uppercase tracking-widest text-zinc-500 mb-2 block">Fighter Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
                placeholder="Enter fighter name (2-40 characters)"
                className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-zinc-100 focus:border-amber-600 outline-none"
              />
            </div>

            <div className="border border-zinc-800 bg-zinc-900/50 p-4 space-y-3">
              <h4 className="font-cinzel text-amber-300 text-lg mb-3">Review Your Fighter</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-zinc-500">Class:</span>
                  <span className="ml-2 text-zinc-200 capitalize">{selectedClass}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Class Specialist:</span>
                  <span className="ml-2 text-zinc-200">{constants.class_specialists.find(s => s.id === classSpecialist)?.name}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Tactic Specialist:</span>
                  <span className="ml-2 text-zinc-200">{constants.tactic_specialists.find(s => s.id === tacticSpecialist)?.name}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Total Stats:</span>
                  <span className="ml-2 text-emerald-400 font-semibold">{totalStats}</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-950/20 border border-amber-700/40 rounded p-3">
              <p className="text-xs text-amber-300">
                ⚠️ Once created, your fighter&apos;s class and specializations cannot be changed. Stats can only be improved through training.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => step > 0 ? setStep(step - 1) : navigate("/arena-circuit")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700 flex items-center gap-2"}
          disabled={busy}
        >
          <ArrowLeft size={16} />
          {step === 0 ? "Cancel" : "Back"}
        </button>

        {step < STEPS.length - 1 ? (
          <button
            onClick={() => setStep(step + 1)}
            className={btn() + " flex items-center gap-2"}
            disabled={!canProgress()}
          >
            Next
            <ArrowRight size={16} />
          </button>
        ) : (
          <button
            onClick={handleCreate}
            className={btn() + " flex items-center gap-2"}
            disabled={!canProgress() || busy}
          >
            <Swords size={16} />
            {busy ? "Creating..." : "Create Fighter"}
          </button>
        )}
      </div>
    </div>
  );
}
